// ABOUTME: Section on My Family page for managing family-level calendar provider connections.
// ABOUTME: Allows parents to add/remove Google, Outlook, CalDAV, and ICS subscription calendar sources.

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import naluV1Theme from '../../theme/theme.nalu-v1';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import GoogleIcon from '@mui/icons-material/Google';
import CloudIcon from '@mui/icons-material/Cloud';
import DnsIcon from '@mui/icons-material/Dns';
import LinkIcon from '@mui/icons-material/Link';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { CalendarConnectionDto, CalendarProviderType } from '../../types/api';
import { calendarAPI } from '../../services/api';
import AddCalendarConnectionModal from './modals/AddCalendarConnectionModal';
import RemoveCalendarConnectionDialog from './modals/RemoveCalendarConnectionDialog';

interface CalendarSourcesSectionProps {
  familyId: number;
  onConnectionsChanged?: () => void;
}

const getProviderIcon = (providerType: CalendarProviderType) => {
  switch (providerType) {
    case CalendarProviderType.Google:
      return <GoogleIcon />;
    case CalendarProviderType.Outlook:
      return <CloudIcon />;
    case CalendarProviderType.CalDAV:
      return <DnsIcon />;
    case CalendarProviderType.IcsSubscription:
      return <LinkIcon />;
    default:
      return <CalendarMonthIcon />;
  }
};

const CalendarSourcesSection: React.FC<CalendarSourcesSectionProps> = ({ familyId: _familyId, onConnectionsChanged }) => {
  const theme = useTheme();
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;
  const [connections, setConnections] = useState<CalendarConnectionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [connectionToRemove, setConnectionToRemove] = useState<CalendarConnectionDto | null>(null);
  const [removing, setRemoving] = useState(false);
  const [refreshingId, setRefreshingId] = useState<number | null>(null);
  const [healthStatus, setHealthStatus] = useState<Record<number, boolean>>({});
  const [requiresReauthMap, setRequiresReauthMap] = useState<Record<number, boolean>>({});
  const [retryingId, setRetryingId] = useState<number | null>(null);
  const [reconnectConnection, setReconnectConnection] = useState<CalendarConnectionDto | null>(null);

  const checkHealthForConnections = useCallback(async (conns: CalendarConnectionDto[]) => {
    const healthResults: Record<number, boolean> = {};
    const reauthResults: Record<number, boolean> = {};
    await Promise.all(
      conns.map(async (conn) => {
        try {
          const health = await calendarAPI.checkConnectionHealth(conn.id);
          healthResults[conn.id] = health.isHealthy;
          reauthResults[conn.id] = health.requiresReauth ?? false;
        } catch {
          healthResults[conn.id] = false;
        }
      })
    );
    setHealthStatus(healthResults);
    setRequiresReauthMap(reauthResults);
  }, []);

  const loadConnections = useCallback(async () => {
    try {
      setLoading(true);
      const data = await calendarAPI.getConnections();
      setConnections(data);
      setError(undefined);
      // Run health checks in the background after loading
      checkHealthForConnections(data);
    } catch {
      setError('Failed to load calendar connections.');
    } finally {
      setLoading(false);
    }
  }, [checkHealthForConnections]);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  const handleRemoveClick = (connection: CalendarConnectionDto) => {
    setConnectionToRemove(connection);
    setRemoveDialogOpen(true);
  };

  const handleConfirmRemove = async () => {
    if (!connectionToRemove) return;

    setRemoving(true);
    try {
      await calendarAPI.deleteConnection(connectionToRemove.id);
      setRemoveDialogOpen(false);
      setConnectionToRemove(null);
      loadConnections();
      onConnectionsChanged?.();
    } catch {
      setError('Failed to remove calendar connection.');
    } finally {
      setRemoving(false);
    }
  };

  const handleConnectionAdded = () => {
    loadConnections();
    onConnectionsChanged?.();
  };

  const handleRefresh = async (connectionId: number) => {
    setRefreshingId(connectionId);
    try {
      await calendarAPI.refreshSources(connectionId);
      await loadConnections();
      onConnectionsChanged?.();
    } catch {
      setError('Failed to refresh calendar sources.');
    } finally {
      setRefreshingId(null);
    }
  };

  const handleRetry = async (connectionId: number) => {
    setRetryingId(connectionId);
    try {
      const health = await calendarAPI.checkConnectionHealth(connectionId);
      setHealthStatus(prev => ({ ...prev, [connectionId]: health.isHealthy }));
      setRequiresReauthMap(prev => ({ ...prev, [connectionId]: health.requiresReauth ?? false }));
      if (health.isHealthy) {
        // If healthy now, also refresh sources to re-discover calendars
        await calendarAPI.refreshSources(connectionId);
        await loadConnections();
        onConnectionsChanged?.();
      }
    } catch {
      setHealthStatus(prev => ({ ...prev, [connectionId]: false }));
    } finally {
      setRetryingId(null);
    }
  };

  const handleReconnect = async (connection: CalendarConnectionDto) => {
    if (connection.providerType === CalendarProviderType.CalDAV) {
      // CalDAV reconnect: open the credential update modal instead of OAuth
      setReconnectConnection(connection);
      return;
    }

    try {
      const redirectUri = `${window.location.origin}/calendar/oauth/callback`;
      const result = await calendarAPI.startOAuth(connection.providerType, redirectUri);
      if (result.authorizationUrl) {
        sessionStorage.setItem('calendar_oauth_state', result.state);
        sessionStorage.setItem('calendar_oauth_provider', String(connection.providerType));
        sessionStorage.setItem('calendar_oauth_reconnect_id', String(connection.id));
        window.location.href = result.authorizationUrl;
      }
    } catch {
      setError('Failed to start reconnection flow.');
    }
  };

  return (
    <Box data-testid="calendar-sources-section">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarMonthIcon color="primary" /> Calendar Sources
        </Typography>
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          size="small"
          onClick={() => setAddModalOpen(true)}
          data-testid="add-calendar-source-button"
          sx={isNaluV1Theme ? { borderRadius: '999px' } : undefined}
        >
          Add Calendar Source
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(undefined)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : connections.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          No calendar sources connected. Add a calendar source to display events on child dashboards.
        </Typography>
      ) : (
        <List disablePadding>
          {connections.map((connection) => {
            const isUnhealthy = healthStatus[connection.id] === false;
            return (
              <ListItem
                key={connection.id}
                data-testid={`calendar-connection-${connection.id}`}
                secondaryAction={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {isUnhealthy ? (
                      <Chip
                        label="Connection failed"
                        size="small"
                        color="error"
                        variant="outlined"
                      />
                    ) : (
                      <Chip
                        label={`${connection.calendarSourceCount} calendar${connection.calendarSourceCount !== 1 ? 's' : ''}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {isUnhealthy && requiresReauthMap[connection.id] ? (
                      <Button
                        variant="outlined"
                        size="small"
                        color="warning"
                        startIcon={<LinkIcon />}
                        onClick={() => handleReconnect(connection)}
                        data-testid={`reconnect-connection-${connection.id}`}
                        aria-label="Reconnect calendar"
                      >
                        Reconnect
                      </Button>
                    ) : isUnhealthy ? (
                      <Tooltip title="Retry connection">
                        <IconButton
                          aria-label="Retry connection"
                          onClick={() => handleRetry(connection.id)}
                          disabled={retryingId === connection.id}
                          data-testid={`retry-connection-${connection.id}`}
                          color="warning"
                        >
                          {retryingId === connection.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <ReplayIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    <Tooltip title="Refresh calendars">
                      <IconButton
                        aria-label="Refresh calendars"
                        onClick={() => handleRefresh(connection.id)}
                        disabled={refreshingId === connection.id}
                        data-testid={`refresh-connection-${connection.id}`}
                      >
                        {refreshingId === connection.id ? (
                          <CircularProgress size={20} />
                        ) : (
                          <RefreshIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove connection">
                      <IconButton
                        edge="end"
                        aria-label="Remove"
                        onClick={() => handleRemoveClick(connection)}
                        data-testid={`remove-connection-${connection.id}`}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
                sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
              >
                <ListItemIcon>{getProviderIcon(connection.providerType)}</ListItemIcon>
                <ListItemText
                  primary={connection.displayName}
                  secondary={connection.externalAccountId}
                />
              </ListItem>
            );
          })}
        </List>
      )}

      <AddCalendarConnectionModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onConnectionAdded={handleConnectionAdded}
      />

      <AddCalendarConnectionModal
        open={reconnectConnection !== null}
        onClose={() => setReconnectConnection(null)}
        onConnectionAdded={() => {
          setReconnectConnection(null);
          loadConnections();
          onConnectionsChanged?.();
        }}
        editingConnection={reconnectConnection ? {
          id: reconnectConnection.id,
          displayName: reconnectConnection.displayName,
          serverUrl: reconnectConnection.externalAccountId,
        } : undefined}
      />

      <RemoveCalendarConnectionDialog
        open={removeDialogOpen}
        connection={connectionToRemove}
        removing={removing}
        onClose={() => {
          setRemoveDialogOpen(false);
          setConnectionToRemove(null);
        }}
        onConfirm={handleConfirmRemove}
      />
    </Box>
  );
};

export default CalendarSourcesSection;
